<script>
import { getProduct } from "@/helpers/contentful";
import ProductImage from "@/components/product/ProductImage";
import ProductInfo from "@/components/product/ProductInfo";

export default {
  name: "Product",

  components: {
    ProductImage,
    ProductInfo,
  },

  data() {
    return {
      product: {},
      currentIndex: 0,
      imageInView: 0,
      showFullscreenImage: false,
      zoom: false,
    };
  },

  created() {
    this.getProduct();
  },

  computed: {
    numOfImages() {
      return this.product.images.length;
    },

    isSingleImage() {
      return this.numOfImages === 1;
    },

    isLastImage() {
      return this.currentIndex + 1 === this.numOfImages;
    },
  },

  methods: {
    openFullscreenImage() {
      this.showFullscreenImage = true;
    },

    closeFullscreenImage() {
      this.showFullscreenImage = false;
    },

    toggleZoom() {
      this.zoom = !this.zoom;
    },

    setImageInView(e) {
      const { scrollLeft } = e.target;
      const { offsetWidth } = this.$refs.slider;
      this.imageInView = Math.round(scrollLeft / offsetWidth);
    },

    scrollToImage(i) {
      const [image] = this.$refs[i];
      this.$refs.slider.scrollTo({
        left: image.offsetLeft,
        behavior: "smooth",
      });

      this.currentIndex = i;
    },

    scrollToIndex(i) {
      let scrollToIndex = i;

      if (i < 0) {
        scrollToIndex = this.numOfImages - 1;
      }

      if (i > this.numOfImages - 1) {
        scrollToIndex = 0;
      }

      const [image] = this.$refs[scrollToIndex];

      this.$refs.slider.scrollTo({
        left: image.offsetLeft,
        behavior: "smooth",
      });

      this.currentIndex = scrollToIndex;
    },

    async getProduct() {
      const slug = this.$route.params.slug;

      try {
        this.product = await getProduct(slug);
      } catch (error) {
        console.error(error);
      }
    },
  },
};
</script>

<template>
  <div v-if="product" class="product">
    <div
      class="slider"
      :class="{ 'scroll-lock': showFullscreenImage }"
      ref="slider"
      @scroll="setImageInView"
      v-if="product.images"
    >
      <div class="slider-images">
        <div
          class="slider-image"
          :class="{ single: isSingleImage }"
          v-for="(image, i) in product.images"
          :ref="i"
          :key="i"
          @click="openFullscreenImage()"
        >
          <div
            class="fullscreen-image-wrapper"
            :class="{ zoom }"
            v-if="showFullscreenImage"
            @click.stop="closeFullscreenImage()"
          >
            <div class="close">
              <div class="line" />
              <div class="line" />
            </div>

            <img
              :class="{ zoom }"
              @click.stop="toggleZoom()"
              :src="product.images[imageInView].fields.file.url"
              alt=""
            />
          </div>

          <ProductImage :image="image" :index="i" />
        </div>
      </div>

      <div class="arrows" v-if="!isSingleImage" :class="{ last: isLastImage }">
        <div class="arrow left" @click="scrollToIndex(currentIndex - 1)" />
        <div class="arrow right" @click="scrollToIndex(currentIndex + 1)" />
      </div>
    </div>

    <ProductInfo :product="product" />

    <div class="buttons desktop" v-if="product.images && !isSingleImage">
      <button
        class="button small"
        :class="{ current: currentIndex === i }"
        v-for="(image, i) in product.images"
        :key="i"
        @click="scrollToImage(i)"
      />
    </div>

    <div class="buttons mobile" v-if="product.images && !isSingleImage">
      <button
        class="button"
        :class="{ current: imageInView === i }"
        v-for="(image, i) in product.images"
        :key="i"
        @click="scrollToImage(i)"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.product {
  position: absolute;
  overflow-y: hidden;
  height: 100%;
  width: 100%;
}

.slider {
  position: relative;
  overflow-x: scroll;
  // -webkit-overflow-scrolling: touch;
  // scroll-snap-type: x mandatory;
  // -ms-overflow-style: none;

  &.scroll-lock {
    overflow: hidden;
  }
}

.arrows {
  display: none;

  @include small {
    height: 100%;
    width: 100%;
    position: fixed;
    display: flex;
    top: 0;

    .arrow {
      height: 100%;

      &.left {
        cursor: url("~@/assets/arrow-left.svg"), auto;
        width: 55%;
      }

      &.right {
        cursor: url("~@/assets/arrow-right.svg"), auto;
        width: 45%;
      }
    }

    &.last {
      .left {
        width: 45%;
      }

      .right {
        display: 55;
      }
    }
  }
}

.buttons {
  position: absolute;
  bottom: $space-base;
  left: 50%;
  transform: translateX(-50%);
  display: flex;

  .button {
    margin-left: 6px;
    height: 12px;
    width: 12px;
    padding: 0;
    border-radius: 100%;
    border: 1px solid black;
    background: transparent;

    &.small {
      margin-left: 8px;
      height: 8px;
      width: 8px;
    }

    &:first-child {
      margin-left: 0;
    }

    &.current {
      background: black;
    }

    &:active,
    &:focus {
      outline: 0;
    }
  }
}

.mobile {
  @include small {
    display: none;
  }
}

.desktop {
  display: none;

  @include small {
    display: block;
  }
}

.slider-images {
  display: flex;
}

.slider-image {
  scroll-snap-align: start;
  flex-shrink: 0;
  width: 100%;
  height: 100vh;

  @include medium {
    width: 55%;

    &.single {
      width: 100%;
    }
  }
}

.fullscreen-image-wrapper {
  overflow: scroll;
  height: 100vh;
  width: 100vw;
  background: rgba(0, 0, 0, 0.8);
  position: fixed;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 999;

  &.zoom {
    display: block;
  }

  img {
    margin-top: -80px;

    &.zoom {
      margin-top: 0px;
      max-width: none;
      width: 240%;
    }
  }

  .close {
    position: fixed;
    top: 14px;
    right: 14px;
    width: 32px;
    height: 32px;

    .line {
      position: absolute;
      top: 14px;
      width: 32px;
      border-bottom: 1px solid white;
      transform: rotateZ(45deg);

      &:nth-child(2) {
        transform: rotateZ(-45deg);
      }
    }
  }
}
</style>
